import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FileText, Download } from "lucide-react";
import { PDFDocument, PDFFont, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { parse, format } from "date-fns";

// The letter is drawn onto public/letter-template.pdf — the signed ACA_IPA
// reference letter with the member-specific lines (date, member bullet, the two
// body paragraphs) redacted out. Everything else — letterhead, recipient block,
// subject heading, "Thanks / Yours faithfully", the President's signature and
// name — is the original artwork, so the output is identical to the reference
// apart from the details below.
const TEMPLATE_URL = "/letter-template.pdf";

// Carlito is metric-compatible with the Calibri the reference letter was typeset
// in, so redrawn lines wrap and sit exactly where the originals did.
const FONT_REGULAR_URL = "/fonts/Carlito-Regular.ttf";
const FONT_BOLD_URL = "/fonts/Carlito-Bold.ttf";

const ACA_FULL = "Association of Certified Accountants, Malaysia";
const ACA_FORMER = "Institute of Commercial & Industrial Accountants";

const BLACK = rgb(0, 0, 0);

// Geometry lifted from the reference letter (612 × 792, y measured from bottom).
const LEFT_X = 93.6;          // body left margin
const WRAP_W = 424.8;         // 612 − 93.6 × 2
const BODY_SIZE = 10.08;
const LINE_H = 12.48;         // leading within a paragraph
const PARA_GAP = 25.44;       // baseline-to-baseline across a blank line
const DATE_BASELINE = 599.28;
const BULLET_BASELINE = 425.52;
const BULLET_TEXT_X = 111.6;  // the "-" sits at LEFT_X, the name is indented
const BODY_BASELINE = 400.08; // first line of "In relation to the above…"

export type Gender = "male" | "female" | null | undefined;

interface LetterProps {
  fullName: string;
  memberNum: string;
  date: string;
  city?: string;
  country?: string;
  gender?: Gender;
}

// Subject pronoun for the closing sentence. With no gender on record we repeat the
// member's name — always correct, never a guess.
const subjectPronoun = (fullName: string, gender: Gender): string =>
  gender === "male" ? "he" : gender === "female" ? "she" : fullName;

// Builds the single-page ACA "Letter of Good Standing" addressed to the IPA
// Membership Chairman, following the ACA_IPA reference letter.
// `city` / `country` are accepted for call-site compatibility; the reference
// letter does not print the member's address.
export const buildLetterPdf = async (
  fullName: string,
  memberNum: string,
  date: string,
  city?: string,
  country?: string,
  gender?: Gender,
): Promise<Uint8Array> => {
  const [templateBytes, regularBytes, boldBytes] = await Promise.all([
    fetch(TEMPLATE_URL).then((r) => {
      if (!r.ok) throw new Error("Letter template not found");
      return r.arrayBuffer();
    }),
    fetch(FONT_REGULAR_URL).then((r) => r.arrayBuffer()),
    fetch(FONT_BOLD_URL).then((r) => r.arrayBuffer()),
  ]);

  const doc = await PDFDocument.load(templateBytes);
  doc.registerFontkit(fontkit);
  // subset:false — pdf-lib's subsetter drops most Carlito glyphs. features: liga
  // off — the "ti"/"fi" ligatures it would otherwise substitute get mis-spaced
  // ("Certi fied", "acti ve").
  const embedOpts = { subset: false, features: { liga: false, dlig: false, clig: false } };
  const font = await doc.embedFont(regularBytes, embedOpts);
  const bold = await doc.embedFont(boldBytes, embedOpts);
  const page = doc.getPages()[0];

  let dateObj: Date;
  try {
    dateObj = parse(date, "MMMM d, yyyy", new Date());
    if (isNaN(dateObj.getTime())) dateObj = new Date();
  } catch {
    dateObj = new Date();
  }

  const draw = (text: string, x: number, y: number, f: PDFFont) =>
    page.drawText(text, { x, y, size: BODY_SIZE, font: f, color: BLACK });

  // A run of body text; `bold` marks the parts the reference letter emboldens
  // (the member's name).
  type Run = { text: string; bold?: boolean };

  // Word-wraps mixed regular/bold runs at WRAP_W and returns the last baseline used.
  const paragraph = (runs: Run[], startY: number): number => {
    const words: Run[] = [];
    for (const run of runs) {
      for (const w of run.text.split(" ").filter(Boolean)) words.push({ text: w, bold: run.bold });
    }

    let y = startY;
    let x = LEFT_X;
    const spaceW = font.widthOfTextAtSize(" ", BODY_SIZE);

    for (const w of words) {
      const f = w.bold ? bold : font;
      const wordW = f.widthOfTextAtSize(w.text, BODY_SIZE);
      const gap = x === LEFT_X ? 0 : spaceW;
      if (x + gap + wordW > LEFT_X + WRAP_W) {
        y -= LINE_H;
        x = LEFT_X;
        draw(w.text, x, y, f);
        x += wordW;
      } else {
        draw(w.text, x + gap, y, f);
        x += gap + wordW;
      }
    }
    return y;
  };

  // ── Date, e.g. "1st August 2026" ──────────────────────────────────────────
  draw(format(dateObj, "do MMMM yyyy"), LEFT_X, DATE_BASELINE, font);

  // ── "-  <name> (Certified Member No. <n>)" ────────────────────────────────
  draw("-", LEFT_X, BULLET_BASELINE, font);
  draw(`${fullName} (Certified Member No. ${memberNum})`, BULLET_TEXT_X, BULLET_BASELINE, bold);

  // ── Body ──────────────────────────────────────────────────────────────────
  const lastY = paragraph(
    [
      { text: "In relation to the above, we would like to confirm that" },
      { text: fullName, bold: true },
      { text: `is still an active member with good standing with ${ACA_FULL} (Formerly known as ${ACA_FORMER}).` },
    ],
    BODY_BASELINE,
  );

  paragraph(
    [
      {
        text: `Therefore, ${subjectPronoun(fullName, gender)} is entitled to enjoy preferential membership application rate with IPA.`,
      },
    ],
    lastY - PARA_GAP,
  );

  const bytes = await doc.save();
  return bytes.slice();
};

// Matches the reference file name, e.g. "ACA_IPA letter_Riyas_August 2026.pdf"
const letterFileName = (fullName: string, date: string): string => {
  let d: Date;
  try {
    d = parse(date, "MMMM d, yyyy", new Date());
    if (isNaN(d.getTime())) d = new Date();
  } catch {
    d = new Date();
  }
  return `ACA_IPA letter_${fullName}_${format(d, "MMMM yyyy")}.pdf`;
};

export const downloadLetter = async (
  fullName: string,
  memberNum: string,
  date: string,
  city?: string,
  country?: string,
  gender?: Gender,
): Promise<void> => {
  const bytes = await buildLetterPdf(fullName, memberNum, date, city, country, gender);
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = letterFileName(fullName, date);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const LetterDownload = ({ fullName, memberNum, date, city, country, gender }: LetterProps) => (
  <Button
    variant="outline"
    size="sm"
    title="Download Letter of Good Standing"
    className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
    onClick={() => downloadLetter(fullName, memberNum, date, city, country, gender)}
  >
    <Download className="h-3.5 w-3.5" />
  </Button>
);

export const InlineLetterViewer = ({ fullName, memberNum, date, city, country, gender }: LetterProps) => {
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let revoke: string | null = null;
    buildLetterPdf(fullName, memberNum, date, city, country, gender).then((bytes) => {
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      revoke = url;
      setPdfUrl(url);
    });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [open, fullName, memberNum, date, city, country, gender]);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        title="View Letter of Good Standing"
        className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
        onClick={() => setOpen(true)}
      >
        <FileText className="h-3.5 w-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden">
          <DialogTitle className="sr-only">Letter of Good Standing</DialogTitle>
          <div className="flex flex-col h-full">
            <div className="flex-1 bg-muted/20">
              {pdfUrl && <iframe src={`${pdfUrl}#toolbar=0&navpanes=0&view=Fit`} title="Letter of Good Standing" className="w-full h-full border-0" />}
            </div>
            <div className="p-3 border-t border-border/50 flex justify-end">
              <Button size="sm" className="gap-1.5" onClick={() => downloadLetter(fullName, memberNum, date, city, country, gender)}>
                <Download className="h-3.5 w-3.5" /> Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LetterDownload;
